/**
 * The environment, parsed once, at startup, or the app does not render.
 *
 * This is the ONLY module in `src/` allowed to read `import.meta.env` — every other module gets
 * its configuration from here. That is what makes "is VITE_X set" have one answer instead of one
 * opinion per call site, and it means a missing variable fails loudly in `main.tsx`, before any
 * component mounts, instead of surfacing later as an unrelated-looking error deep in a component
 * (an empty Firebase `apiKey` fails as `auth/invalid-api-key` on the login screen, for example —
 * a config fault that reads like an auth bug).
 *
 * Hand-rolled rather than a schema library on purpose: this module sits in the entry chunk (it is
 * imported before anything else), and all it checks is "are these strings non-empty" — a handful
 * of lines does that without adding a dependency to the one chunk that is never code-split.
 */

/** Required at boot. A missing one of these is a deployment fault, not a degraded feature. */
const REQUIRED = [
  'VITE_API_URL',
  // Firebase Auth is the only Firebase service this app initialises (src/shared/firebase.ts).
  // storageBucket / messagingSenderId / measurementId configure Cloud Storage, FCM and Analytics
  // — none of which this platform uses (file uploads go through DO Spaces, mirroring
  // driver-document — see docs/superpowers/specs/2026-10-13-storage-design.md §2 in
  // nexvia-api-web). Requiring fields the app never reads would refuse a correct environment.
  'VITE_APP_FIREBASE_API_KEY',
  'VITE_APP_FIREBASE_AUTH_DOMAIN',
  'VITE_APP_FIREBASE_PROJECT_ID',
  'VITE_APP_FIREBASE_APP_ID'
] as const;

export type Env = Readonly<Record<(typeof REQUIRED)[number], string>>;

/** Vite substitutes an unset variable as `undefined`; a bare `KEY=` line in .env yields `''`. */
const present = (value: unknown): value is string => typeof value === 'string' && value.trim() !== '';

/** Exported so a test can exercise a failing record without crashing the runner. */
export const parseEnv = (source: unknown): Env => {
  const raw = (source ?? {}) as Record<string, unknown>;
  const missing = REQUIRED.filter((name) => !present(raw[name]));

  if (missing.length > 0) {
    throw new Error(
      `Configuración inválida: faltan ${[...missing].sort().join(', ')}. ` +
        'Revisá el .env de este entorno — .env.example lista las claves que la app necesita.'
    );
  }

  const parsed: Record<string, string> = {};
  for (const name of REQUIRED) parsed[name] = String(raw[name]).trim();
  return Object.freeze(parsed) as Env;
};

export const env = parseEnv(import.meta.env);

/**
 * Optional on/off switches: unset (or anything but `'true'`) means off, so a missing one is a
 * hidden feature, never a boot failure. Read at call time (the default argument is evaluated per
 * call), so a test can flip one with `vi.stubEnv`.
 */
export type EnvFlag = 'VITE_EMAIL_PASSWORD_AUTH';

export const isEnvFlagOn = (name: EnvFlag, source: unknown = import.meta.env): boolean =>
  (source as Record<string, unknown> | undefined)?.[name] === 'true';

/**
 * Optional values: unset or blank means "not configured" (`null`), never a boot failure — the
 * feature that needs one degrades instead. Read at call time, like the flags above.
 *
 * NEX-1168 — `VITE_APP_GOOGLE_MAPS_API_KEY`: the Google Maps JavaScript API key behind the address
 * autocomplete of "Mis depósitos" (`src/shared/google-maps-options.ts`) — same name as nexvia-web-app's
 * (renamed from `VITE_APP_GOOGLE_MAPS_KEY`, 2026-10-20). Without it the address is
 * typed by hand, with no coordinates, exactly as before.
 */
export type OptionalEnvValue = 'VITE_APP_GOOGLE_MAPS_API_KEY';

export const optionalEnvValue = (name: OptionalEnvValue, source: unknown = import.meta.env): string | null => {
  const value = (source as Record<string, unknown> | undefined)?.[name];
  return present(value) ? value.trim() : null;
};
